import { GridLayout, useTracks } from "@livekit/components-react";
import { Track } from "livekit-client";
import { Users } from "lucide-react";
import { useMemo } from "react";
import { useStore } from "zustand";
import { Card } from "@/components/ui/card";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { sortTracksByActivity } from "@/lib/livekit-utils";
import { coreStore } from "@/lib/stores/core-store";
import { cn } from "@/lib/utils";
import { CustomParticipantTile } from "./custom-participant-tile";
import { MoveNotificationOverlay } from "./move-notification-overlay";

type StageVideoGridProps = {
	channelId: string;
	variant?: "full" | "compact";
};

export function StageVideoGrid({
	channelId,
	variant = "full",
}: StageVideoGridProps) {
	const speakerTileClass = "aspect-video h-auto";

	const stageStates = useStore(coreStore, (state) => state.stageStates);
	const stageState = stageStates.get(channelId);
	const speakerIds = useMemo(
		() => new Set(stageState?.speakers ?? []),
		[stageState?.speakers]
	);

	const trackSources = useMemo(
		() => [
			{ source: Track.Source.Camera, withPlaceholder: true },
			{ source: Track.Source.ScreenShare, withPlaceholder: false },
		],
		[]
	);

	const rawTracks = useTracks(trackSources, { onlySubscribed: false });
	const tracks = useMemo(() => sortTracksByActivity(rawTracks), [rawTracks]);

	const screenShareTrack = useMemo(
		() => tracks.find((track) => track.source === Track.Source.ScreenShare),
		[tracks]
	);

	const speakerTracks = useMemo(
		() =>
			tracks.filter(
				(track) =>
					track.source !== Track.Source.ScreenShare &&
					speakerIds.has(track.participant.identity)
			),
		[tracks, speakerIds]
	);

	const audienceTracks = useMemo(
		() =>
			tracks.filter(
				(track) =>
					track.source !== Track.Source.ScreenShare &&
					!speakerIds.has(track.participant.identity)
			),
		[tracks, speakerIds]
	);

	const hasAudience = audienceTracks.length > 0;
	const hasSpeakers = speakerTracks.length > 0 || screenShareTrack;

	if (screenShareTrack) {
		return (
			<div className="relative flex h-full w-full flex-col overflow-hidden bg-muted">
				<div className="flex flex-1 gap-0 overflow-hidden">
					<Card className="relative flex flex-1 items-center justify-center gap-0 overflow-hidden p-0">
						<div className="[&_.lk-participant-tile]:!border-0 h-full w-full [&_.lk-participant-metadata]:hidden [&_.lk-participant-name]:hidden [&_.lk-participant-tile]:h-full [&_.lk-participant-tile]:w-full [&_video]:h-full [&_video]:w-full [&_video]:object-contain">
							<CustomParticipantTile trackRef={screenShareTrack} />
						</div>
					</Card>
					{hasSpeakers && (
						<ScrollArea className="w-40 shrink-0">
							<div className="flex flex-col gap-0 p-0">
								{speakerTracks.map((track) => (
									<Card
										className={cn(
											"aspect-video w-full shrink-0 overflow-hidden",
											"[&_.lk-participant-name]:mx-1 [&_.lk-participant-name]:mb-1 [&_.lk-participant-name]:max-w-[10ch]",
											"[&_.lk-participant-name]:overflow-hidden [&_.lk-participant-name]:text-ellipsis [&_.lk-participant-name]:whitespace-nowrap",
											"gap-0 p-0 [&_.lk-participant-name]:text-xs [&_.lk-participant-tile]:h-full [&_.lk-participant-tile]:w-full",
											"[&_.lk-participant-name]:text-white"
										)}
										key={`${track.participant.sid}-${track.source}`}
									>
										<CustomParticipantTile
											className={speakerTileClass}
											trackRef={track}
										/>
									</Card>
								))}
							</div>
						</ScrollArea>
					)}
				</div>
				{hasAudience && (
					<AudienceRow audienceTracks={audienceTracks} variant={variant} />
				)}
				<MoveNotificationOverlay />
			</div>
		);
	}

	if (!hasSpeakers) {
		return (
			<div className="relative flex h-full w-full flex-col overflow-hidden bg-muted">
				<div className="flex flex-1 flex-col items-center justify-center gap-4">
					<div className="flex size-20 items-center justify-center rounded-full bg-muted-foreground/10">
						<Users className="size-10 text-muted-foreground" />
					</div>
					<p className="text-muted-foreground text-sm">The stage is empty</p>
				</div>
				{hasAudience && (
					<AudienceRow audienceTracks={audienceTracks} variant={variant} />
				)}
				<MoveNotificationOverlay />
			</div>
		);
	}

	return (
		<div className="relative flex h-full w-full flex-col overflow-hidden bg-muted">
			<div className="flex-1 overflow-hidden p-2">
				<GridLayout
					className="[&_.lk-participant-name]:mx-1 [&_.lk-participant-name]:mb-1 [&_.lk-participant-name]:max-w-full [&_.lk-participant-name]:overflow-hidden [&_.lk-participant-name]:text-ellipsis [&_.lk-participant-name]:whitespace-nowrap [&_.lk-participant-name]:text-white [&_.lk-participant-name]:text-xs [&_.lk-participant-tile]:rounded-lg [&_.lk-participant-tile]:border [&_.lk-participant-tile]:bg-card!"
					style={{ height: "100%", width: "100%" }}
					tracks={speakerTracks}
				>
					<CustomParticipantTile className={speakerTileClass} />
				</GridLayout>
			</div>
			{hasAudience && (
				<AudienceRow audienceTracks={audienceTracks} variant={variant} />
			)}
			<MoveNotificationOverlay />
		</div>
	);
}

type AudienceRowProps = {
	audienceTracks: ReturnType<typeof useTracks>;
	variant: "full" | "compact";
};

function AudienceRow({ audienceTracks, variant }: AudienceRowProps) {
	const tileSize = variant === "compact" ? "h-16 w-16" : "h-24 w-24";

	return (
		<div className="shrink-0 border-t bg-card/50 p-2">
			<ScrollArea className="w-full">
				<div className="flex gap-2">
					{audienceTracks.map((track) => (
						<Card
							className={cn(
								tileSize,
								"shrink-0 overflow-hidden",
								"[&_.lk-participant-name]:hidden",
								"gap-0 p-0 [&_.lk-participant-tile]:h-full [&_.lk-participant-tile]:w-full"
							)}
							key={`${track.participant.sid}-${track.source}`}
						>
							<CustomParticipantTile hideMutedIndicator trackRef={track} />
						</Card>
					))}
				</div>
				<ScrollBar orientation="horizontal" />
			</ScrollArea>
		</div>
	);
}
